import { useAtomValue } from 'jotai';

import { Stack, Tag } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';

import { useGetOrderDetails } from '../../api/useGetOrderDetails';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';
import { getRiskLevelAccent } from '../../utils/risk';

import { SectionSkeleton } from './shared/SectionSkeleton';

const SECTION_TITLE = 'Risk review';

/**
 * Map verification status to Tag accent color
 */
const getVerificationStatusAccent = (
	status: string | undefined,
): 'neutral' | 1 | 2 | 3 | 4 | 5 | 6 | 7 => {
	if (!status) return 'neutral';

	const normalizedStatus = status.toLowerCase().replace(/-/g, '_');

	switch (normalizedStatus) {
		case 'verified':
			return 3; // Teal/Green
		case 'processing':
		case 'requires_input':
		case 'requires input':
			return 5; // Orange
		case 'failure':
		case 'state_mismatch':
		case 'state mismatch':
			return 4; // Pink/Red
		case 'not_started':
		case 'not started':
		case 'created':
		case 'canceled':
			return 'neutral'; // Grey
		default:
			return 'neutral';
	}
};

export const RiskReviewSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderDetails, isLoading } = useGetOrderDetails(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!orderDetails) {
		return null;
	}

	const { riskReview } = orderDetails;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='12'>
				<InfoLineItem
					title='Risk level'
					content={
						<Tag size='medium' accent={getRiskLevelAccent(riskReview.riskLevel)}>
							{riskReview.riskLevel}
						</Tag>
					}
				/>
				<InfoLineItem
					title='Verification status'
					content={
						<Tag
							size='medium'
							accent={getVerificationStatusAccent(riskReview.verificationStatus)}>
							{riskReview.verificationStatus}
						</Tag>
					}
				/>
				<InfoLineItem
					title='Manually verified'
					value={riskReview.manuallyVerified ? 'Yes' : 'No'}
				/>
				{riskReview.notes && <InfoLineItem title='Notes' value={riskReview.notes} />}
			</Stack>
		</AccordionSection>
	);
};
